import { useState } from "react";
import type { ChangeEvent } from "react";
import { articleUnits, cancelledIds, reasonLabel } from "@lagerlog/core";
import type { Booking, Entity } from "@lagerlog/core";
import { useStore } from "../hooks.ts";
import { download, euroCell, toCsv, today } from "../download.ts";
import { messageOf } from "./Login.tsx";
import { APP, IS_PANTRY, WORDS } from "../brand.ts";

interface Export {
  format: string;
  version: number;
  exportedAt: number;
  workspace: { name: string };
  entities: Entity[];
  bookings: Booking[];
}

const KIND: Record<Booking["kind"], string> = { in: "Zugang", out: "Abgang", move: "Umlagerung", count: "Zählung", convert: "Umwandlung", cancel: "Storno" };

/** The bookings of an export as a table for Excel: one row per booking, cancelled ones marked. */
function bookingsCsv(data: Export, person: (id: string | undefined) => string): string {
  const names = new Map<string, string>();
  for (const e of data.entities) names.set(`${e.type}|${e.id}`, typeof e.fields.name === "string" ? e.fields.name : e.id);
  const n = (type: string, id: string | undefined) => (id ? (names.get(`${type}|${id}`) ?? id) : "");
  const cancelled = cancelledIds(data.bookings);
  // Quantities in bottles for a drink (stored in ml), else in the base unit; kegs and cans say so in a column.
  const units = new Map<string, { factor: number; name: string }>();
  for (const e of data.entities) if (e.type === "article") units.set(e.id, articleUnits(e.fields).units[0]!);
  const q = (article: string, qty: number) => qty / (units.get(article)?.factor ?? 1);
  const mixed = !IS_PANTRY && [...units.values()].some((u) => u.factor > 1 && u.name !== WORDS.unitShort);
  const rows: unknown[][] = [["Zeitpunkt", "Art", WORDS.one, "Lager", "Lagerort", "nach Lager", "nach Lagerort", mixed ? "Menge" : `Menge (${WORDS.unitShort})`, "Grund", "Einkaufspreis gesamt (€)", "Person", "Gerät", "storniert", "Kennung", ...(mixed ? ["Gebinde"] : [])]];
  for (const b of data.bookings) {
    const at = new Date(b.at).toLocaleString("de-DE");
    const unit = "article" in b ? (units.get(b.article)?.name ?? "") : b.kind === "convert" ? (units.get(b.fromArticle)?.name ?? "") : "";
    const common = [person(b.person), b.device ?? "", cancelled.has(b.id) && b.kind !== "cancel" ? "ja" : "", b.id, ...(mixed ? [unit] : [])];
    switch (b.kind) {
      case "in":
      case "out":
        rows.push([at, KIND[b.kind], n("article", b.article), n("lager", b.lager), n("location", b.location), "", "", q(b.article, b.kind === "out" ? -b.qty : b.qty), reasonLabel(b.reason), b.kind === "in" && b.amount !== undefined ? euroCell(b.amount) : "", ...common]);
        break;
      case "move":
        rows.push([at, KIND.move, n("article", b.article), n("lager", b.fromLager), n("location", b.from), n("lager", b.toLager), n("location", b.to), q(b.article, b.qty), "", "", ...common]);
        break;
      case "count":
        rows.push([at, KIND.count, n("article", b.article), n("lager", b.lager), n("location", b.location), "", "", q(b.article, b.qty), "", "", ...common]);
        break;
      case "convert":
        rows.push([at, KIND.convert, `${n("article", b.fromArticle)} → ${n("article", b.toArticle)}`, n("lager", b.lager), n("location", b.location), "", "", `${q(b.fromArticle, b.fromQty)} → ${q(b.toArticle, b.toQty)}`, "", "", ...common]);
        break;
      case "cancel":
        rows.push([at, KIND.cancel, "", "", "", "", "", "", `nimmt ${b.cancels} zurück`, "", ...common]);
    }
  }
  return toCsv(rows);
}

/**
 * The complete export (Z6, ID20): everything as JSON, the bookings as a table, and the way back
 * into a workspace without bookings. Any time and without asking the operator.
 */
export function Data() {
  const { store } = useStore();
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const name = store.config?.workspace ?? APP.short;

  const fetchAll = () => store.request<Export>("/api/export", {}, 300_000);

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setError(null);
    setMessage(null);
    try {
      await fn();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(null);
    }
  }

  const json = () =>
    run("json", async () => {
      const data = await fetchAll();
      download(`${name} ${today()}.json`, JSON.stringify(data), "application/json");
      setMessage(`${data.bookings.length} Buchungen und ${data.entities.length} Stammdaten gesichert.`);
    });
  const csv = () =>
    run("csv", async () => {
      const data = await fetchAll();
      download(`${name} Buchungen ${today()}.csv`, bookingsCsv(data, (id) => store.personName(id) ?? ""), "text/csv;charset=utf-8");
    });

  function restore(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void run("import", async () => {
      const data = JSON.parse(await file.text()) as Export;
      const res = await store.request<{ entities: number; bookings: number }>("/api/import", { method: "POST", body: JSON.stringify(data) }, 300_000);
      await store.reload();
      setMessage(`Eingespielt: ${res.bookings} Buchungen, ${res.entities} Stammdaten.`);
    });
  }

  // Export and import are for owners (F20); the server refuses them to everyone else.
  if (store.rights().role !== "owner") return null;
  return (
    <section className="card" data-testid="data">
      <h2>Daten mitnehmen</h2>
      <p className="muted">Alles, was {store.config?.kind === "private" ? (IS_PANTRY ? "dieser Haushalt" : "dieser Keller") : "dieser Betrieb"} je gebucht hat: jederzeit, ohne Rückfrage. Nur für Inhaber.</p>
      <div className="buttons">
        <button disabled={busy !== null} onClick={json} data-testid="export-json">
          {busy === "json" ? "Lädt …" : "Alles sichern (JSON)"}
        </button>
        <button disabled={busy !== null} onClick={csv} data-testid="export-csv">
          {busy === "csv" ? "Lädt …" : "Buchungen als Excel-Tabelle (CSV)"}
        </button>
      </div>
      <details>
        <summary>Sicherung einspielen …</summary>
        <p className="hint">Nur in einen Betrieb ohne Buchungen, etwa nach einem Umzug. Danach lädt dieses Gerät alles neu.</p>
        <label className="button">
          {busy === "import" ? "Spielt ein …" : "Sicherung wählen"}
          <input type="file" accept=".json,application/json" hidden onChange={restore} data-testid="import-file" />
        </label>
      </details>
      {message && <p className="ok-text">{message}</p>}
      {error && <p className="error-box">{error}</p>}
    </section>
  );
}
